import * as CompetitionApi from '../../api/competition'
import * as CompanyApi from '../../api/company'
import router from '../../router'
import { competitionTypes, getReadableCompetitionType } from '../../constants/competitionTypes'
import { CURRENCY } from '../../constants/currency'
import {
  COMPETITION_FIELD_TYPE,
  COMPETITION_SPECIAL_FILTER
} from '../../views/Competitions/models/competitionFieldType'
import { LOGIC_OPERATOR } from '../../views/Competitions/models/logicOperator'
import {
  convertFieldForms,
  convertBooleanServerResponse,
  convertArrayServerResponse,
  convertNumberServerResponse,
  convertFinanceDataServerResponse,
  convertAnnualDataServerResponse
} from '../../converters/fieldFormConverter'
import { competitionSubTypes, getReadableCompetitionSubType } from '../../constants/competitionSubTypes'

const convertStepThreeServerResponse = (payload, state) => {
  const result = JSON.parse(JSON.stringify(state.values.stepThree.fieldForms))
  let requirementFilters = {}
  if (payload.requirementFilters) {
    requirementFilters = payload.requirementFilters[0].conditions[0]
  }
  if (requirementFilters === undefined) {
    return result
  }
  Object.keys(requirementFilters).forEach(key => {
    const currentFieldForm = state.values.stepThree.fieldForms[key].fieldForm
    let currentConditions = [...state.values.stepThree.fieldForms[key].conditions]
    const responseConditions = requirementFilters[key].conditions

    if (COMPETITION_SPECIAL_FILTER.SPECIAL_FINANCE_DATA_FIELDS.includes(currentFieldForm.field)) {
      currentConditions = convertFinanceDataServerResponse(responseConditions)
      result[key].conditions = currentConditions
    } else if (COMPETITION_SPECIAL_FILTER.SPECIAL_ANNUAL_DATA_FIELDS.includes(currentFieldForm.field)) {
      currentConditions = convertAnnualDataServerResponse(responseConditions)
      currentConditions.forEach(condition => {
        condition.currency = CURRENCY.HUF
      })
      result[key].conditions = currentConditions
    } else {
      switch (currentFieldForm.type.mainType) {
        case COMPETITION_FIELD_TYPE.NUMBER: {
          currentConditions = convertNumberServerResponse(responseConditions, currentFieldForm)
          result[key].conditions = currentConditions
          break
        }
        case COMPETITION_FIELD_TYPE.ARRAY: {
          currentConditions = convertArrayServerResponse(responseConditions)
          result[key].conditions = currentConditions
          break
        }
        case COMPETITION_FIELD_TYPE.BOOLEAN: {
          currentConditions = convertBooleanServerResponse(responseConditions)
          result[key].conditions = currentConditions
          break
        }
      }
    }
  })
  return result
}

const convertStepThreeData = stepThree => {
  const conditions = convertFieldForms(stepThree.fieldForms)
  return {
    requirementFilters: [{ logic: LOGIC_OPERATOR.AND, conditions }],
    complexFilters: stepThree.complexFilters.map(cf => cf._id)
  }
}

const stringifyStep = step => {
  switch (step) {
    case 1: {
      return 'stepOne'
    }
    case 2: {
      return 'stepTwo'
    }
    case 3: {
      return 'stepThree'
    }
    default:
      throw Error('Hibás lépés a pályázat felvitele közben')
  }
}

const getStepTwoData = values => {
  const creditValues = ['creditLength', 'creditAmount', 'creditInterest']
  const guaranteeValues = ['guaranteeCost', 'guaranteeMax', 'guaranteeLength']
  const applicationValues = [
    'preQualification',
    'preQualificationStart',
    'preQualificationEnd',
    'preQualificationLink',
    'subsidy'
  ]
  const capitalValues = ['capitalAnnualCost', 'capitalAmount', 'capitalInvestmentLength', 'capitalLength']
  switch (values.type.value) {
    case competitionTypes.CREDIT: {
      const result = {
        ...values,
        type: values.type ? values.type.value : null,
        subType: values.subType ? values.subType.value : null,
        step: 2
      }
      guaranteeValues
        .concat(applicationValues)
        .concat(capitalValues)
        .forEach(key => {
          delete result[key]
        })
      return result
    }
    case competitionTypes.APPLICATION: {
      const result = {
        ...values,
        type: values.type ? values.type.value : null,
        subType: values.subType ? values.subType.value : null,
        step: 2
      }
      guaranteeValues
        .concat(creditValues)
        .concat(capitalValues)
        .forEach(key => {
          delete result[key]
        })
      if (!values.preQualification) {
        delete result.preQualificationStart
        delete result.preQualificationLink
        delete result.preQualificationEnd
      }
      return result
    }
    case competitionTypes.CAPITAL_PRODUCT: {
      const result = {
        ...values,
        type: values.type ? values.type.value : null,
        subType: values.subType ? values.subType.value : null,
        step: 2
      }
      guaranteeValues
        .concat(creditValues)
        .concat(applicationValues)
        .forEach(key => {
          delete result[key]
        })
      return result
    }
    case competitionTypes.GUARANTEE: {
      const result = {
        ...values,
        type: values.type ? values.type.value : null,
        subType: values.subType ? values.subType.value : null,
        step: 2
      }
      capitalValues
        .concat(creditValues)
        .concat(applicationValues)
        .forEach(key => {
          delete result[key]
        })
      return result
    }
  }
}

const getDataOfStep = (state, step) => {
  switch (step) {
    case 1: {
      return { ...state.stepOne, step }
    }
    case 2: {
      const values = getStepTwoData(state.stepTwo)
      return values
    }
    case 3: {
      return { ...convertStepThreeData(state.stepThree), step }
    }
  }
}

const setStep = (state, payload) => (state.step = payload)

const setFieldForms = (state, payload) => {
  let fieldForms = {}
  let errors = {}
  payload.forEach(fieldForm => {
    fieldForms = { ...fieldForms, [fieldForm.field]: { fieldForm, conditions: [] } }
    errors = { ...errors, [fieldForm.field]: [] }
  })
  state.values.stepThree = { ...state.values.stepThree, fieldForms }
  state.errors.stepThree.fieldForms = errors
}

const setLoading = (state, loading) => (state.loading = loading)

const setFieldForm = (state, { value, conditionIndex }) => {
  const errors = [...state.errors.stepThree.fieldForms[value.fieldForm.field]]
  const currentError = [...errors.filter(error => error.index !== conditionIndex)]
  state.errors.stepThree.fieldForms[value.fieldForm.field] = currentError
  state.values.stepThree.fieldForms[value.fieldForm.field] = value
}

const setId = (state, payload) => (state.id = payload)

const setValues = (state, payload) => {
  Object.keys(state.values.stepOne).forEach(key => {
    state.values.stepOne[key] = payload[key]
  })
  Object.keys(state.values.stepTwo).forEach(key => {
    state.values.stepTwo[key] = payload[key]
  })
  if (router.history.current.path === '/competitions/competition/3') {
    state.values.stepThree.complexFilters = payload.complexFilters
    state.values.stepThree.fieldForms = convertStepThreeServerResponse(payload, state)
  }
}

const setValue = (state, { step, key, value }) => {
  state.values[step][key] = value
  state.errors[step][key] = []
}

const setType = (state, type) => {
  switch (type.value) {
    case competitionTypes.CREDIT: {
      state.values.stepTwo.creditLength = ''
      state.values.stepTwo.creditAmount = ''
      state.values.stepTwo.creditInterest = ''
      break
    }
    case competitionTypes.GUARANTEE: {
      state.values.stepTwo.guaranteeCost = ''
      state.values.stepTwo.guaranteeLength = ''
      state.values.stepTwo.guaranteeMax = ''
      break
    }
    case competitionTypes.CAPITAL_PRODUCT: {
      state.values.stepTwo.capitalAmount = ''
      state.values.stepTwo.capitalAnnualCost = ''
      state.values.stepTwo.capitalInvestmentLength = ''
      state.values.stepTwo.capitalLength = ''
      break
    }
    case competitionTypes.APPLICATION: {
      state.values.stepTwo.preQualification = false
      state.values.stepTwo.preQualificationEnd = undefined
      state.values.stepTwo.preQualificationStart = undefined
      state.values.stepTwo.preQualificationLink = undefined
      break
    }
  }
  state.values.stepTwo.type = type
}

const updateErrors = (state, { step, errors }) => (state.errors[step] = { ...state.errors[step], ...errors })

const changeStepThreeErrorsToEmpty = stepThreeErrors => {
  const errors = { ...stepThreeErrors }

  Object.keys(errors).forEach(key => {
    if (key !== 'fieldForms') {
      errors[key] = []
    } else {
      Object.keys(errors.fieldForms).forEach(fieldFormError => {
        errors.fieldForms[fieldFormError] = []
      })
    }
  })
  return errors
}

const clearErrors = state => {
  state.errors.stepOne = initialState.errors.stepOne
  state.errors.stepTwo = initialState.errors.stepTwo
  state.errors.stepThree = changeStepThreeErrorsToEmpty(state.errors.stepThree)
}

const resetState = state => {
  Object.assign(state, JSON.parse(JSON.stringify(initialState)))
}

const clearTypeRelatedData = state => {
  state.stepTwo = {
    ...state.stepTwo,
    preQualification: false,
    preQualificationStart: null,
    preQualificationEnd: null,
    type: null,
    subsidy: '',
    creditLength: '',
    creditAmount: '',
    creditInterest: '',
    guaranteeCost: '',
    guaranteeMax: '',
    guaranteeLength: '',
    capitalAmount: '',
    capitalInvestmentLength: '',
    capitalLength: ''
  }
}

function handleStepThreeErrors(commit, state, dispatch, error) {
  let errors = {}
  let fieldFormErrors = { ...state.errors.stepThree.fieldForms }
  Object.keys(state.errors.stepThree).map(key => {
    errors[key] = []
  })
  Object.keys(state.values.stepThree.fieldForms).forEach(key => {
    fieldFormErrors[key] = []
  })
  error.details.forEach(e => {
    if (
      e.path[0] === 'requirementFilters' &&
      e.path[1] === 0 &&
      e.path[2] === 'conditions' &&
      e.type === 'any.required'
    ) {
      dispatch('notify', {
        newNotification: { type: 'warning', message: e.message }
      })
    }
    if (e.path[0] === 'requirementFilters') {
      if (e.path[5] === 'conditions') {
        const keyOfWrongFilter = e.path[4]
        const positionOfWrongCondition = e.path[6]
        fieldFormErrors[keyOfWrongFilter].push({ index: positionOfWrongCondition, message: e.message })
      }
    } else {
      errors[e.context.key] = [...errors[e.context.key], e.message]
    }
  })
  errors = { ...errors, fieldForms: fieldFormErrors }
  return errors
}

function handleError(commit, state, dispatch, { step, error }) {
  const stringStep = stringifyStep(step)
  if (step === 3) {
    const errors = handleStepThreeErrors(commit, state, dispatch, error)
    commit('updateErrors', { step: 'stepThree', errors })
  } else {
    let errors = { ...initialState.errors[stringStep] }
    error.details.forEach(e => {
      errors[e.context.key] = [...errors[e.context.key], e.message]
    })
    commit('updateErrors', { step: stringStep, errors })
  }
}

function goToNextStep({ state }, step) {
  const path = `/competitions/competition/${step}`
  router.push({ path, query: { competitionId: state.id } })
}

async function getCompetition({ commit }, { competitionId }) {
  commit('setLoading', true)
  commit('setId', competitionId)
  try {
    const competition = await CompetitionApi.getCompetition(competitionId)
    const type = competition.type
      ? { value: competition.type, name: getReadableCompetitionType(competition.type) }
      : null
    const subType = competition.subType
      ? { value: competition.subType, name: getReadableCompetitionSubType(competition.subType) }
      : null
    commit('setValues', {
      ...competition,
      type,
      subType
    })
    commit('setLoading', false)
  } catch (e) {
    console.log(e)
  }
}

async function initializeState({ state, commit, dispatch }, { step }) {
  if (step === 1) {
    if (state.id === null) {
      commit('resetState')
    } else {
      dispatch('getCompetition', { competitionId: state.id })
      router.replace({ query: { competitionId: state.id } })
    }
  } else {
    dispatch('getCompetition', { competitionId: state.id })
  }
}

async function loadFieldForms({ commit }) {
  const result = await CompanyApi.getCompanyFieldFormTemplates()
  commit('setFieldForms', result)
}

async function saveCompetition({ state, commit, dispatch }, { step }) {
  commit('clearErrors')
  try {
    if (step === 1 && state.id === null) {
      const data = await CompetitionApi.createCompetition(getDataOfStep(state.values, step))
      commit('setId', data._id)
      commit('clearErrors')
      dispatch('goToNextStep', step + 1)
    } else {
      const values = getDataOfStep(state.values, step)
      await CompetitionApi.editCompetiton(values, state.id)
      commit('clearErrors')
      if (step === 3) {
        const path = `/competitions/details`
        router.push({ path, query: { id: state.id } })
      } else {
        dispatch('goToNextStep', step + 1)
      }
    }
  } catch (e) {
    if (e.response && e.response.status === 400) {
      handleError(commit, state, dispatch, { step: state.step, error: e.response.data })
    } else {
      throw e
    }
  }
}

async function sendValidationRequest({ state, commit }) {
  commit('clearErrors')
  await CompetitionApi.editCompetiton(getDataOfStep(state.values, state.step), state.id)
}

async function rejectValidationRequest({ commit, dispatch }, { competitionId, rejectionReason }) {
  commit('clearErrors')

  await CompetitionApi.validateCompetition(
    {
      rejectionReason: rejectionReason,
      isRejection: true
    },
    competitionId
  )
  dispatch('notify', { newNotification: { type: 'success', message: 'Pályázat visszaküldve módosításra.' } })
}

const getters = {
  id: state => state.id,
  step: state => state.step,
  competitionLoading: state => state.loading,
  stepOneValues: state => state.values.stepOne,
  stepOneErrors: state => state.errors.stepOne,
  stepTwoValues: state => state.values.stepTwo,
  stepTwoErrors: state => state.errors.stepTwo,
  stepThreeValues: state => state.values.stepThree,
  stepThreeErrors: state => state.errors.stepThree,
  getFieldFormErrors: state => ({ key, index }) => {
    if (
      state.errors.stepThree.fieldForms[key] === undefined ||
      state.errors.stepThree.fieldForms[key].filter(fieldForm => fieldForm.index === index).length === 0
    ) {
      return []
    } else {
      return state.errors.stepThree.fieldForms[key]
        .filter(fieldForm => fieldForm.index === index)
        .map(error => error.message)
    }
  }
}

const initialState = {
  values: {
    stepOne: {
      name: '',
      code: '',
      link: '',
      releaseDateStart: null,
      releaseDateEnd: null
    },
    stepTwo: {
      text: '',
      qualificationStart: '',
      qualificationEnd: '',
      stateSubsidy: '',
      preQualification: false,
      preQualificationStart: null,
      preQualificationEnd: null,
      preQualificationLink: '',
      type: null,
      subType: { name: '', value: competitionSubTypes.EMPTY },
      subsidy: '',
      creditLength: '',
      creditAmount: '',
      creditInterest: '',
      guaranteeCost: '',
      guaranteeMax: '',
      guaranteeLength: '',
      capitalAnnualCost: '',
      capitalAmount: '',
      capitalInvestmentLength: '',
      capitalLength: ''
    },
    stepThree: {
      fieldForms: {},
      complexFilters: []
    }
  },
  errors: {
    stepOne: { name: [], code: [], link: [], releaseDateStart: [], releaseDateEnd: [] },
    stepTwo: {
      text: [],
      type: [],
      subType: [],
      qualificationStart: [],
      qualificationEnd: [],
      preQualification: [],
      preQualificationStart: [],
      preQualificationEnd: [],
      preQualificationLink: [],
      stateSubsidy: [],
      subsidy: [],
      creditLength: [],
      creditAmount: [],
      creditInterest: [],
      guaranteeCost: [],
      guaranteeMax: [],
      guaranteeLength: [],
      capitalAnnualCost: [],
      capitalAmount: [],
      capitalInvestmentLength: [],
      capitalLength: []
    },
    stepThree: {
      fieldForms: {},
      complexFilters: []
    }
  },
  id: null,
  step: 1,
  loading: false
}

const selectedCompetitionStore = {
  state: {
    ...initialState,
    values: {
      stepOne: { ...initialState.values.stepOne },
      stepTwo: { ...initialState.values.stepTwo },
      stepThree: { ...initialState.values.stepThree }
    },
    errors: {
      stepOne: { ...initialState.errors.stepOne },
      stepTwo: { ...initialState.errors.stepTwo },
      stepThree: { ...initialState.errors.stepThree }
    }
  },
  mutations: {
    setStep,
    setValue,
    setType,
    setValues,
    setId,
    updateErrors,
    clearTypeRelatedData,
    clearErrors,
    resetState,
    setFieldForms,
    setFieldForm,
    setLoading
  },
  actions: {
    saveCompetition,
    goToNextStep,
    getCompetition,
    initializeState,
    loadFieldForms,
    sendValidationRequest,
    rejectValidationRequest
  },
  getters
}

export default selectedCompetitionStore
